"use client";

import React from "react";
import { useTranslations } from "next-intl";
import {
  MdApartment,
  MdHome,
  MdLandscape,
  MdBusiness,
  MdCheckCircle,
  MdErrorOutline,
} from "react-icons/md";
import {
  PurposeEnum,
  TypeEnum,
  ApartmentCategoryEnum,
  HouseCategoryEnum,
  LandCategoryEnum,
  OfficeCategoryEnum,
  BuildingTypeEnum,
  OfficeTypeEnum,
} from "@/types/types";
import styles from "./basic-info.module.css";
import { useListingContext } from "../listingContex";

export default function BasicInfoSection() {
  const t = useTranslations("NewListing");
  const { data, updateField, updateMultipleFields, validationErrors } =
    useListingContext();

  if (!data) return <BasicInfoSkeleton />;

  const handleTypeChange = (newType: TypeEnum) => {
    updateMultipleFields({
      type: newType,
      category: null,
      building_type: null,
      office_type: null,
    });
  };

  const getCategoryOptions = () => {
    if (data.type === TypeEnum.APARTMENT)
      return Object.values(ApartmentCategoryEnum);
    if (data.type === TypeEnum.HOUSE) return Object.values(HouseCategoryEnum);
    if (data.type === TypeEnum.LAND) return Object.values(LandCategoryEnum);
    if (data.type === TypeEnum.OFFICE) return Object.values(OfficeCategoryEnum);
    return [];
  };

  const categoryOptions = getCategoryOptions();
  const showBuildingType =
    data.type === TypeEnum.APARTMENT || data.type === TypeEnum.OFFICE;

  return (
    <section className={styles.transparentContainer}>
      <h2 className={styles.title}>Basic Informations</h2>

      {/* PURPOSE TOGGLE */}
      <div className={styles.groupWrapper} id="purpose">
        <div className={styles.purposeToggleWrapper}>
          <div className={styles.purposeToggle}>
            <button
              className={`${styles.purposeBtn} ${data.purpose === PurposeEnum.FORSALE ? styles.purposeActive : ""}`}
              onClick={(e) => {
                e.preventDefault();
                updateField("purpose", PurposeEnum.FORSALE);
              }}
            >
              {t("forSale")}
            </button>
            <button
              className={`${styles.purposeBtn} ${data.purpose === PurposeEnum.FORRENT ? styles.purposeActive : ""}`}
              onClick={(e) => {
                e.preventDefault();
                updateField("purpose", PurposeEnum.FORRENT);
              }}
            >
              {t("forRent")}
            </button>
          </div>
        </div>
        {validationErrors?.purpose && (
          <span className={styles.inlineErrorStandalone}>
            <MdErrorOutline /> {t(`errors.${validationErrors.purpose}`)}
          </span>
        )}
      </div>

      {/* TYPE CARDS */}
      <div className={styles.groupWrapper} id="type">
        <div className={styles.typeGrid}>
          <TypeCard
            icon={<MdApartment />}
            text={t("apartament")}
            isActive={data.type === TypeEnum.APARTMENT}
            onClick={() => handleTypeChange(TypeEnum.APARTMENT)}
          />
          <TypeCard
            icon={<MdHome />}
            text={t("house")}
            isActive={data.type === TypeEnum.HOUSE}
            onClick={() => handleTypeChange(TypeEnum.HOUSE)}
          />
          <TypeCard
            icon={<MdLandscape />}
            text={t("land")}
            isActive={data.type === TypeEnum.LAND}
            onClick={() => handleTypeChange(TypeEnum.LAND)}
          />
          <TypeCard
            icon={<MdBusiness />}
            text={t("offices")}
            isActive={data.type === TypeEnum.OFFICE}
            onClick={() => handleTypeChange(TypeEnum.OFFICE)}
          />
        </div>
        {validationErrors?.type && (
          <span className={styles.inlineErrorStandalone}>
            <MdErrorOutline /> {t(`errors.${validationErrors.type}`)}
          </span>
        )}
      </div>

      {/* DYNAMIC CATEGORY & BUILDING TYPE (White Shadow Pills) */}
      {(categoryOptions.length > 0 || showBuildingType) && (
        <div className={styles.dynamicRow}>
          {categoryOptions.length > 0 && (
            <div id="category" className={styles.inputGroup}>
              <div className={styles.labelWithInfo}>
                <label className={styles.inputLabel}>
                  {t("Category.categoryTitle")}{" "}
                  <span className={styles.asterisk}>*</span>
                </label>
                {validationErrors?.category && (
                  <span className={styles.inlineError}>
                    <MdErrorOutline />{" "}
                    {t(`errors.${validationErrors.category}`)}
                  </span>
                )}
              </div>
              <div
                className={`${styles.pillGrid} ${validationErrors?.category ? styles.gridError : ""}`}
              >
                {categoryOptions.map((opt) => (
                  <button
                    key={opt}
                    className={`${styles.whitePill} ${data.category === opt ? styles.whitePillActive : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      updateField("category", opt as any);
                    }}
                  >
                    {t(`Category.${opt}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showBuildingType && (
            <div id="building_type" className={styles.inputGroup}>
              <div className={styles.labelWithInfo}>
                <label className={styles.inputLabel}>
                  {data.type === TypeEnum.APARTMENT
                    ? t("buildingType.apartmentTitle")
                    : t("buildingType.officeTitle")}
                </label>
                {validationErrors?.[
                  data.type === TypeEnum.APARTMENT
                    ? "building_type"
                    : "office_type"
                ] && (
                  <span className={styles.inlineError}>
                    <MdErrorOutline />{" "}
                    {t(
                      `errors.${validationErrors[data.type === TypeEnum.APARTMENT ? "building_type" : "office_type"]}`,
                    )}
                  </span>
                )}
              </div>
              <div
                className={`${styles.pillGrid} ${validationErrors?.[data.type === TypeEnum.APARTMENT ? "building_type" : "office_type"] ? styles.gridError : ""}`}
              >
                {(data.type === TypeEnum.APARTMENT
                  ? Object.values(BuildingTypeEnum)
                  : Object.values(OfficeTypeEnum)
                ).map((opt) => (
                  <button
                    key={opt}
                    className={`${styles.whitePill} ${(data.type === TypeEnum.APARTMENT ? data.building_type : data.office_type) === opt ? styles.whitePillActive : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      updateField(
                        data.type === TypeEnum.APARTMENT
                          ? "building_type"
                          : "office_type",
                        opt as any,
                      );
                    }}
                  >
                    {t(`buildingType.${opt}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function TypeCard({
  icon,
  text,
  isActive,
  onClick,
}: {
  icon: any;
  text: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`${styles.typeCard} ${isActive ? styles.typeActive : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {isActive && (
        <div className={styles.checkBadge}>
          <MdCheckCircle size={20} />
        </div>
      )}
      <div className={styles.typeIcon}>{icon}</div>
      <span className={styles.typeText}>{text}</span>
    </button>
  );
}

export function BasicInfoSkeleton() {
  return (
    <section className={styles.transparentContainer}>
      {/* Title */}
      <div className={`${styles.skeletonPulse} ${styles.skelTitle}`} />

      {/* Purpose Toggle */}
      <div className={styles.purposeToggleWrapper}>
        <div className={`${styles.skeletonPulse} ${styles.skelPurpose}`} />
      </div>

      {/* Type Grid (4 Cards) */}
      <div className={styles.typeGrid}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`${styles.skeletonPulse} ${styles.skelTypeCard}`}
          />
        ))}
      </div>

      {/* Dynamic Row (Category & Building Type) */}
      <div className={styles.dynamicRow}>
        <div className={styles.inputGroup}>
          <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
          <div className={styles.pillGrid}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`${styles.skeletonPulse} ${styles.skelPill}`}
              />
            ))}
          </div>
        </div>

        <div className={styles.inputGroup}>
          <div
            className={`${styles.skeletonPulse} ${styles.skelLabel}`}
            style={{ width: "180px" }}
          />
          <div className={styles.pillGrid}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`${styles.skeletonPulse} ${styles.skelPill} ${styles.skelPillWide}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
